import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  LockedIcon,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Permission, PermissionAction, StudentServiceTemplate } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const StudentServiceTemplatePageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  reroute: (id: number, crudMode: CrudMode) => void;
  fetch: (id: number) => void;
  template: StudentServiceTemplate;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.template.id;
  const saveEntriesCount = props.template.createAdditionalEntries
    ? props.template.createAdditionalEntries.length + 1
    : 1;

  const save = (template: StudentServiceTemplate) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.studentServiceTemplates
        .saveUpdate(template)
        .then(() => {
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    } else {
      ApiService.studentServiceTemplates
        .saveCreate(template)
        .then((response) => {
          const id = response;

          if (id && id > 0) {
            props.reroute(id, "view");
            props.fetch(id);
          } else {
            navigate("../");
          }

          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.studentServiceTemplates.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (
      isAuthorized(props.rootPermission, PermissionAction.Delete) &&
      (!props.template.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock))
    )
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">
          {id === 0 ? "New" : ""} {props.handle}
        </Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.template.isLocked && <LockedIcon />}

        {id > 0 && <DataModelInformation dataModelType={DataModelEnum.StudentServiceTemplate} dataModelId={id} />}

        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "view" &&
          isAuthorized(props.rootPermission, PermissionAction.Update) &&
          (!props.template.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
            <UpdateButton onClick={() => props.reroute(id, "edit")} />
          )}

        {props.crudMode === "edit" && (
          <SubmitButton
            onClick={() => save(props.template)}
            disabled={!props.unsavedChanges}
            label={saveEntriesCount > 1 ? `Save (${saveEntriesCount} entries)` : undefined}
          />
        )}
      </Stack>
    </Stack>
  );
};
